import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getAllUsers, getSeedContentHidden } from '../../services/firestoreService';
import { getUserConnections, addConnection, removeConnection } from '../../services/connectionService';
import { SEED_MEMBERS } from '../../data/seedMembers';

const MyNetwork = () => {
  const { currentUser } = useAuth();
  // Network data (loaded from the member's real connections)
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [connectionsPerPage] = useState(12);
  const [suggestedConnections, setSuggestedConnections] = useState([]);

  // Load the member's real network + real "People You May Know" suggestions
  // (replaces the old 100-row random mock).
  useEffect(() => {
    let cancelled = false;

    const toDisplay = (u, id) => ({
      id,
      name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.displayName || 'Member',
      title: u.title || u.jobTitle || '',
      company: u.company || u.organization || '',
      industry: u.industry || u.specialty || '',
      image: u.image || u.photoURL || u.profileImage || '',
      mutualConnections: u.mutualConnections || 0,
    });

    const load = async () => {
      setLoading(true);
      try {
        if (!currentUser) {
          if (!cancelled) { setConnections([]); setSuggestedConnections([]); }
          return;
        }

        // Real connections (stored on the member's own user doc as `network`).
        const net = await getUserConnections(currentUser.uid);
        const myConnections = (net || []).map((c) => toDisplay(c, c.userId || c.id));
        const connectedIds = new Set(myConnections.map((c) => c.id));

        // "People You May Know" = real approved members (+ demo members unless
        // hidden), excluding yourself and anyone you're already connected to.
        const [allUsers, seedHidden] = await Promise.all([
          getAllUsers(100).catch(() => []),
          getSeedContentHidden().catch(() => false),
        ]);
        const realPeople = (allUsers || [])
          .filter((u) => u.status === 'approved' && u.role !== 'admin' && u.id !== currentUser.uid && !connectedIds.has(u.id))
          .map((u) => toDisplay(u, u.id));
        const seedPeople = seedHidden
          ? []
          : SEED_MEMBERS.filter((s) => !connectedIds.has(s.id)).map((s) => toDisplay(s, s.id));

        if (!cancelled) {
          setConnections(myConnections);
          setSuggestedConnections([...realPeople, ...seedPeople]);
        }
      } catch (error) {
        console.error('Error loading network:', error);
        if (!cancelled) { setConnections([]); setSuggestedConnections([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Get unique industries for filter
  const industries = [...new Set(connections.map(connection => connection.industry))];

  // Filter connections based on search and industry
  const filteredConnections = connections.filter(connection => {
    const matchesSearch = connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         connection.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         connection.company.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesIndustry = industryFilter === '' || connection.industry === industryFilter;
    
    return matchesSearch && matchesIndustry;
  });

  // Pagination
  const indexOfLastConnection = currentPage * connectionsPerPage;
  const indexOfFirstConnection = indexOfLastConnection - connectionsPerPage;
  const currentConnections = filteredConnections.slice(indexOfFirstConnection, indexOfLastConnection);
  const totalPages = Math.ceil(filteredConnections.length / connectionsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Remove a connection (persists to the member's network).
  const handleRemoveConnection = async (connectionId) => {
    if (!currentUser) return;
    setConnections(prev => prev.filter(connection => connection.id !== connectionId));
    try {
      await removeConnection(currentUser.uid, connectionId);
    } catch (error) {
      console.error('Error removing connection:', error);
    }
  };

  // Add a suggested connection (persists). Demo (seed) members can't be added.
  const handleAddConnection = async (connection) => {
    if (!currentUser) { alert('Please log in to connect.'); return; }
    if (String(connection.id).startsWith('seed-')) {
      alert('This is a demo member. You can connect with real members as they join.');
      return;
    }
    setConnections(prev => [...prev, connection]);
    setSuggestedConnections(prev => prev.filter(suggested => suggested.id !== connection.id));
    try {
      await addConnection(currentUser.uid, {
        userId: connection.id,
        id: connection.id,
        name: connection.name,
        title: connection.title,
        company: connection.company,
        mutualConnections: connection.mutualConnections || 0
      });
    } catch (error) {
      console.error('Error adding connection:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-apn-blue"></div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Page Header */}
      <div className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* APN Logo */}
            <div className="flex justify-center mb-4">
              <img
                src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-white.jpg`}
                alt="APN Logo"
                className="h-16"
              />
            </div>
            <h1 className="text-3xl font-bold">My Network</h1>
            <p className="mt-2 text-lg">Manage your professional connections</p>
          </div>
        </div>
      </div>

      {/* Network Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Network Stats */}
            <div className="bg-gray-50 p-6 border-b border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">{connections.length}</h3>
                  <p className="text-gray-600">Total Connections</p>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">{industries.length}</h3>
                  <p className="text-gray-600">Industries</p>
                </div>
                <div>
                  <h3 className="text-3xl font-bold text-gray-900">{suggestedConnections.length}</h3>
                  <p className="text-gray-600">Suggested Connections</p>
                </div>
              </div>
            </div>

            {/* Filters and Search */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
                <div className="w-full md:w-2/3">
                  <label htmlFor="search" className="sr-only">Search</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      id="search"
                      type="text"
                      placeholder="Search by name, title, or company"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to first page on search
                      }}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-apn-blue focus:border-apn-blue sm:text-sm"
                    />
                  </div>
                </div>
                <div className="w-full md:w-1/3">
                  <label htmlFor="industry" className="sr-only">Filter by Industry</label>
                  <select
                    id="industry"
                    value={industryFilter}
                    onChange={(e) => {
                      setIndustryFilter(e.target.value);
                      setCurrentPage(1); // Reset to first page on filter change
                    }}
                    className="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-apn-blue focus:border-apn-blue sm:text-sm"
                  >
                    <option value="">All Industries</option>
                    {industries.map((industry, index) => (
                      <option key={index} value={industry}>{industry}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Connections List */}
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Your Connections ({filteredConnections.length})</h2>
              
              {filteredConnections.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">No connections found</h3>
                  <p className="mt-1 text-gray-500">Try adjusting your search or filter to find connections.</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {currentConnections.map((connection) => (
                      <div key={connection.id} className="border border-gray-200 rounded-lg overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 mr-4">
                              <img
                                src={`${process.env.PUBLIC_URL}${connection.image}`}
                                alt={connection.name}
                                className="h-16 w-16 rounded-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`;
                                }}
                              />
                            </div>
                            <div className="flex-grow">
                              <h3 className="text-lg font-semibold text-gray-900">{connection.name}</h3>
                              <p className="text-gray-600">{connection.title}</p>
                              <p className="text-gray-500 text-sm">{connection.company}</p>
                              <p className="text-gray-500 text-xs mt-1">{connection.industry}</p>
                            </div>
                          </div>
                          <div className="mt-4 flex justify-between">
                            <Link
                              to={`/directory/${connection.id}`}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 transition ease-in-out duration-150"
                            >
                              View Profile
                            </Link>
                            <button
                              onClick={() => handleRemoveConnection(connection.id)}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:border-red-300 focus:shadow-outline-red active:bg-red-100 transition ease-in-out duration-150"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-8 flex justify-center">
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => paginate(currentPage > 1 ? currentPage - 1 : 1)}
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Previous</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                        
                        {/* Page numbers */}
                        {[...Array(totalPages)].map((_, index) => {
                          const pageNumber = index + 1;
                          // Show current page, first page, last page, and pages around current page
                          if (
                            pageNumber === 1 ||
                            pageNumber === totalPages ||
                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                          ) {
                            return (
                              <button
                                key={pageNumber}
                                onClick={() => paginate(pageNumber)}
                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                  currentPage === pageNumber
                                    ? 'z-10 bg-apn-blue border-apn-blue text-white'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                }`}
                              >
                                {pageNumber}
                              </button>
                            );
                          }
                          
                          // Show ellipsis for skipped pages
                          if (
                            (pageNumber === 2 && currentPage > 3) ||
                            (pageNumber === totalPages - 1 && currentPage < totalPages - 2)
                          ) {
                            return (
                              <span
                                key={pageNumber}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                              >
                                ...
                              </span>
                            );
                          }
                          
                          return null;
                        })}
                        
                        <button
                          onClick={() => paginate(currentPage < totalPages ? currentPage + 1 : totalPages)}
                          disabled={currentPage === totalPages}
                          className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                            currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          <span className="sr-only">Next</span>
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Suggested Connections */}
          {suggestedConnections.length > 0 && (
            <div className="mt-8 bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">People You May Know</h2>
                <p className="text-gray-600">Connect with professionals in your industry</p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {suggestedConnections.map((connection) => (
                    <div key={connection.id} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                      <div className="p-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 mr-4">
                            <img
                              src={`${process.env.PUBLIC_URL}${connection.image}`}
                              alt={connection.name}
                              className="h-16 w-16 rounded-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `${process.env.PUBLIC_URL}/images/profiles/default-avatar.png`;
                              }}
                            />
                          </div>
                          <div className="flex-grow">
                            <h3 className="text-lg font-semibold text-gray-900">{connection.name}</h3>
                            <p className="text-gray-600">{connection.title}</p>
                            <p className="text-gray-500 text-sm">{connection.company}</p>
                            <p className="text-gray-500 text-xs mt-1">{connection.mutualConnections} mutual connections</p>
                          </div>
                        </div>
                        <div className="mt-4 flex justify-between">
                          <Link
                            to={`/directory/${connection.id}`}
                            className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm leading-5 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 transition ease-in-out duration-150"
                          >
                            View Profile
                          </Link>
                          <button
                            onClick={() => handleAddConnection(connection)}
                            className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:border-green-700 focus:shadow-outline-green active:bg-green-800 transition ease-in-out duration-150"
                          >
                            <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Connect
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-apn-blue text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* APN Logo */}
          <div className="flex justify-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/apn-logos/APN-Tag-SQ-white.jpg`}
              alt="APN Logo Square"
              className="h-20"
            />
          </div>
          <h2 className="text-3xl font-bold mb-4">Expand Your Network</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Discover more professionals in your industry by exploring the member directory.
          </p>
          <Link to="/directory" className="btn-primary bg-white text-apn-blue hover:bg-gray-100">
            Browse Member Directory
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MyNetwork;
